import React, { useState, useEffect } from 'react';
import { supabase, getAdminClient } from '../../lib/supabase';
import { toast } from 'sonner';

interface Team {
  id: string;
  name: string;
  seed?: number;
}

interface Match {
  id: string;
  round: number;
  match_number: number;
  team1_id: string | null;
  team2_id: string | null;
  winner_id: string | null;
  team1_score: number | null;
  team2_score: number | null;
  team1?: {
    id: string;
    name: string;
    seed?: number;
  } | null;
  team2?: {
    id: string;
    name: string;
    seed?: number;
  } | null;
}

const NewAdminBracket: React.FC = (): JSX.Element => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [rounds, setRounds] = useState<Record<number, Match[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [totalRounds, setTotalRounds] = useState(0);

  // Fetch teams from the database
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const { data, error } = await supabase
          .from('teams')
          .select('id, name')
          .order('name', { ascending: true });

        if (error) throw error;
        setTeams(data || []);
      } catch (error) {
        console.error('Error fetching teams:', error);
        toast.error('Failed to load teams');
      }
    };

    fetchTeams();
  }, []);

  // Fetch matches from the database
  const fetchMatches = async () => {
    try {
      const adminClient = getAdminClient();
      const { data: matchesData, error: matchesError } = await adminClient
        .from('tournament_matches')
        .select(`
          *,
          team1:team1_id (id, name),
          team2:team2_id (id, name)
        `)
        .order('round', { ascending: true })
        .order('match_number', { ascending: true });

      if (matchesError) throw matchesError;

      // Group matches by round
      const matchesByRound: Record<number, Match[]> = {};
      (matchesData || []).forEach((match: any) => {
        if (!matchesByRound[match.round]) {
          matchesByRound[match.round] = [];
        }
        matchesByRound[match.round].push(match);
      });

      // Find the maximum round number
      const maxRound = Math.max(...Object.keys(matchesByRound).map(Number), 0);
      
      setRounds(matchesByRound);
      setMatches(matchesData || []);
      setTotalRounds(maxRound);
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Failed to load tournament matches');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchMatches();
  }, []);

  // State for manual team selection
  const [selectedTeams, setSelectedTeams] = useState<{team1: string | null; team2: string | null}[]>([
    { team1: null, team2: null },
    { team1: null, team2: null },
    { team1: null, team2: null },
    { team1: null, team2: null },
    { team1: null, team2: null },
    { team1: null, team2: null },
    { team1: null, team2: null },
    { team1: null, team2: null }
  ]);

  // Handle team selection change
  const handleTeamSelect = (matchIndex: number, team: 'team1' | 'team2', teamId: string | null) => {
    const updatedSelections = [...selectedTeams];
    updatedSelections[matchIndex] = {
      ...updatedSelections[matchIndex],
      [team]: teamId
    };
    setSelectedTeams(updatedSelections);
  };

  // Add a new match slot
  const addMatchSlot = () => {
    if (selectedTeams.length < 16) { // Max 16 teams (8 matches) for a 4-round bracket
      setSelectedTeams([...selectedTeams, { team1: null, team2: null }]);
    }
  };

  // Remove a match slot
  const removeMatchSlot = (index: number) => {
    if (selectedTeams.length > 1) {
      const updated = [...selectedTeams];
      updated.splice(index, 1);
      setSelectedTeams(updated);
    }
  };

  // Generate initial bracket with selected teams
  const generateBracket = async () => {
    // Filter out any empty matches
    const validMatches = selectedTeams.filter(match => match.team1 || match.team2);
    
    if (validMatches.length < 1) {
      toast.error('Please select at least one match with teams');
      return;
    }

    // Check that all selected matches have both teams
    const incompleteMatches = validMatches.filter(match => !match.team1 || !match.team2);
    if (incompleteMatches.length > 0) {
      toast.error('Please select both teams for each match');
      return;
    }

    setIsGenerating(true);
    try {
      // Get admin client
      const adminClient = getAdminClient();
      
      // Clear existing matches using admin client
      const { error: deleteError } = await adminClient
        .from('tournament_matches')
        .delete()
        .neq('id', '0');

      if (deleteError) throw deleteError;

      // Calculate number of rounds needed (power of 2)
      const matchCount = validMatches.length;
      const rounds = Math.ceil(Math.log2(matchCount * 2)); // Ensure power of 2 for bracket

      // Generate first round matches
      const newMatches = validMatches.map((match, index) => ({
        id: `r1m${index + 1}`,
        round: 1,
        match_number: index + 1,
        team1_id: match.team1,
        team2_id: match.team2,
        team1_score: null,
        team2_score: null,
        winner_id: null
      }));

      // Save matches to database using admin client
      const { error } = await adminClient
        .from('tournament_matches')
        .insert(newMatches);

      if (error) throw error;

      toast.success('Bracket generated successfully!');
      fetchMatches();
    } catch (error) {
      console.error('Error generating bracket:', error);
      toast.error('Failed to generate bracket');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate next round matches
  const generateNextRound = async (currentRound: number) => {
    console.log('Generating next round. Current round:', currentRound);
    
    // Prevent generating beyond round 4 (Finals)
    if (currentRound >= 4) {
      toast.error('Cannot generate more rounds. The tournament is complete.');
      return;
    }
    
    try {
      const adminClient = getAdminClient();
      
      // Get all matches from the current round
      const { data: currentMatches, error: matchesError } = await adminClient
        .from('tournament_matches')
        .select('*')
        .eq('round', currentRound)
        .order('match_number', { ascending: true });

      if (matchesError) throw matchesError;
      
      if (!currentMatches || currentMatches.length === 0) {
        throw new Error('No matches found for the current round');
      }

      // Check if all matches in the current round have winners
      const incompleteMatches = currentMatches.filter(match => !match.winner_id);
      if (incompleteMatches.length > 0) {
        toast.error('Please complete all matches in the current round first');
        return;
      }

      const nextRound = currentRound + 1;
      console.log('Creating round:', nextRound);

      // Check if next round already exists
      const { data: existingNextRound, error: nextRoundError } = await adminClient
        .from('tournament_matches')
        .select('id')
        .eq('round', nextRound)
        .limit(1);

      if (nextRoundError) throw nextRoundError;
      
      if (existingNextRound && existingNextRound.length > 0) {
        toast.info(`Round ${nextRound} already exists`);
        return;
      }

      // Create next round matches
      const nextRoundMatches = [];
      let matchNumber = 1;

      // Pair winners for the next round
      for (let i = 0; i < currentMatches.length; i += 2) {
        const match1 = currentMatches[i];
        const match2 = currentMatches[i + 1];

        // Only create a match if we have at least one winner
        if (match1.winner_id) {
          nextRoundMatches.push({
            id: `r${nextRound}m${matchNumber}`,
            round: nextRound,
            match_number: matchNumber,
            team1_id: match1.winner_id,
            team2_id: match2?.winner_id || null,
            team1_score: null,
            team2_score: null,
            winner_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          matchNumber++;
        }
      }

      if (nextRoundMatches.length === 0) {
        toast.info('No valid matches to generate for the next round');
        return;
      }

      console.log('Next round matches to create:', nextRoundMatches);

      // Save next round matches
      const { error: insertError } = await adminClient
        .from('tournament_matches')
        .insert(nextRoundMatches);

      if (insertError) throw insertError;

      // Update total rounds if needed
      if (nextRound > totalRounds) {
        setTotalRounds(nextRound);
      }

      toast.success(`Round ${nextRound} generated successfully!`);
      fetchMatches();
      
    } catch (error) {
      console.error('Error in generateNextRound:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate next round');
    }
  };

  // Update match result
  const updateMatchResult = async (matchId: string, winner: 'team1' | 'team2', score1: number, score2: number) => {
    try {
      const adminClient = getAdminClient();
      const match = matches.find(m => m.id === matchId);
      
      if (!match) {
        throw new Error('Match not found');
      }

      const updates = {
        team1_score: score1,
        team2_score: score2,
        winner_id: winner === 'team1' ? match.team1_id : match.team2_id,
        updated_at: new Date().toISOString()
      };

      const { error } = await adminClient
        .from('tournament_matches')
        .update(updates)
        .eq('id', matchId);

      if (error) throw error;
      
      toast.success('Match result updated!');
      fetchMatches();
    } catch (error) {
      console.error('Error updating match result:', error);
      toast.error('Failed to update match result');
    }
  };

  // Use the existing rounds state that's already being set in fetchMatches

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Loading Tournament Bracket...</h1>
          <p className="text-gray-400">Please wait while we load the tournament data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold">Tournament Bracket</h1>
          <div className="flex space-x-4">
            <button
              onClick={addMatchSlot}
              disabled={selectedTeams.length >= 8}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              Add Match
            </button>
            <button
              onClick={generateBracket}
              disabled={isGenerating || matches.length > 0}
              className={`px-4 py-2 rounded-md ${
                matches.length > 0 ? 'bg-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {isGenerating ? 'Generating...' : 'Generate Bracket'}
            </button>
            {matches.length > 0 && (
              <button
                onClick={() => generateNextRound(Math.max(...matches.map(m => m.round)))}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md"
              >
                Generate Next Round
              </button>
            )}
          </div>
        </div>

        {/* Team Selection Interface */}
        {matches.length === 0 && (
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Create First Round Matches</h2>
            <p className="text-gray-400 mb-4">Select teams for each match in the first round (up to 8 matches):</p>
            
            <div className="space-y-4">
              {selectedTeams.map((match, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-gray-700 rounded-lg">
                  <span className="w-8 text-gray-400">Match {index + 1}</span>
                  
                  <select
                    value={match.team1 || ''}
                    onChange={(e) => handleTeamSelect(index, 'team1', e.target.value || null)}
                    className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                  >
                    <option value="">Select Team 1</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  
                  <span className="text-gray-400">vs</span>
                  
                  <select
                    value={match.team2 || ''}
                    onChange={(e) => handleTeamSelect(index, 'team2', e.target.value || null)}
                    className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                  >
                    <option value="">Select Team 2</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  
                  {selectedTeams.length > 1 && (
                    <button
                      onClick={() => removeMatchSlot(index)}
                      className="ml-2 p-2 text-red-400 hover:text-red-300"
                      title="Remove match"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-8">
          {Object.entries(rounds).map(([round, roundMatches]) => (
            <div key={`round-${round}`} className="bg-gray-800 p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-semibold mb-4">Round {round}</h2>
              <div className="space-y-4">
                {roundMatches.map((match) => (
                  <div key={match.id} className="bg-gray-700 p-4 rounded-md">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className={`flex items-center justify-between mb-2 ${match.winner_id === match.team1_id ? 'text-green-400 font-medium' : ''}`}>
                          <span>{match.team1?.name || 'TBD'}</span>
                          <span className="px-2 py-1 bg-gray-600 rounded text-sm">
                            {match.team1_score ?? '-'}
                          </span>
                        </div>
                        <div className={`flex items-center justify-between ${match.winner_id === match.team2_id ? 'text-green-400 font-medium' : ''}`}>
                          <span>{match.team2?.name || 'TBD'}</span>
                          <span className="px-2 py-1 bg-gray-600 rounded text-sm">
                            {match.team2_score ?? '-'}
                          </span>
                        </div>
                      </div>
                      {!match.winner_id && match.team1 && match.team2 && (
                        <div className="ml-4 flex flex-col space-y-2">
                          <input
                            type="number"
                            placeholder="Score 1"
                            className="w-16 px-2 py-1 bg-gray-600 rounded text-white"
                            onChange={(e) => {
                              const score = parseInt(e.target.value) || 0;
                              updateMatchResult(
                                match.id,
                                score > (parseInt(match.team2_score as any) || 0) ? 'team1' : 'team2',
                                score,
                                match.team2_score || 0
                              );
                            }}
                          />
                          <input
                            type="number"
                            placeholder="Score 2"
                            className="w-16 px-2 py-1 bg-gray-600 rounded text-white"
                            onChange={(e) => {
                              const score = parseInt(e.target.value) || 0;
                              updateMatchResult(
                                match.id,
                                score > (parseInt(match.team1_score as any) || 0) ? 'team2' : 'team1',
                                match.team1_score || 0,
                                score
                              );
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewAdminBracket;
